import React, { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import Image from 'ui3/Image/Image';
import { openStoreOrAppUrl } from 'helpers/openAppUrl';
import { isAndroidUserAgent, isMobileUserAgent } from 'helpers/userAgent';
import {
  APPLE_APP_STORE_URL,
  GOOGLE_PLAY_STORE_URL,
} from 'constants/external-links';
import styles from './SmartAppBanner.module.scss';

const BANNER_HEIGHT = '46px';
// Match $breakpoint_l: banner only on mobile viewport so desktop never shows it or uses its height
const MOBILE_VIEWPORT_MAX_WIDTH = 1023;

const SmartAppBanner = (): JSX.Element | null => {
  const [visible, setVisible] = useState(false);
  const { pathname } = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
      return;

    const isMobileUA = isMobileUserAgent();

    const applyBanner = () => {
      const isMobileViewport = window.innerWidth <= MOBILE_VIEWPORT_MAX_WIDTH;
      if (isMobileUA && isMobileViewport) {
        document.body.classList.add('has-smart-banner');
        document.documentElement.style.setProperty(
          '--smart-banner-height',
          BANNER_HEIGHT
        );
        setVisible(true);
      } else {
        document.body.classList.remove('has-smart-banner');
        document.documentElement.style.removeProperty('--smart-banner-height');
        setVisible(false);
      }
    };

    applyBanner();
    window.addEventListener('resize', applyBanner);
    return () => {
      window.removeEventListener('resize', applyBanner);
      document.body.classList.remove('has-smart-banner');
      document.documentElement.style.removeProperty('--smart-banner-height');
    };
  }, [pathname]);

  const handleGetApp = useCallback((): void => {
    if (typeof window === 'undefined') return;
    const url = isAndroidUserAgent()
      ? GOOGLE_PLAY_STORE_URL
      : APPLE_APP_STORE_URL;
    openStoreOrAppUrl(url, false);
  }, []);

  if (!visible) return null;

  return (
    <>
      <div className={styles.banner} role="banner">
        <div className={styles.content}>
          <div className={styles.logoWrap}>
            <Image
              src="/app_icon.svg"
              alt=""
              width={33}
              height={33}
              className={styles.logo}
              unoptimized
            />
          </div>
          <div className={styles.text}>
            <span className={styles.appName}>MILLIONS.co</span>
            <span className={styles.tagline}>Open in the MILLIONS app</span>
          </div>
        </div>
        <button type="button" className={styles.cta} onClick={handleGetApp}>
          GET APP
        </button>
      </div>
      <div className={styles.spacer} aria-hidden />
    </>
  );
};

export default SmartAppBanner;
